import { Editor, Element, Node, Path, Range, Transforms } from 'slate';
import { v4 as uuidv4 } from 'uuid';
import { FootnoteElement, TagElement } from 'slate/types/slate';
import { isTag } from './navigation';
import { isFootnote } from './footnote';
import { writeBlockBindings } from './blockTagging';
import { getBlockTagBindings, getBlockTagIds, TagBinding, TEXT_BLOCK_TAG_CARRIER_TYPES } from '../types/block';

export interface TagSplit {
  tagId: string;
  newTagId: string;
  onId: string | null;
}

export type TagContinuationBlocker = 'numeric' | 'hidden' | 'permission' | 'unsupported';

export interface TagSplitResult {
  splits: TagSplit[];
  blocked: { tagId: string; reason: TagContinuationBlocker }[];
}

export interface TagsAtBreak {
  inline: string[];
  block: string[];
  footnotes: string[];
  carrierUuid: string | null;
}

const isTextCarrier = (n: Node): boolean => Element.isElement(n) && TEXT_BLOCK_TAG_CARRIER_TYPES.has(n.type);
const isTaggedTextCarrier = (n: Node): boolean => isTextCarrier(n) && getBlockTagBindings(n).length > 0;
const isBlank = (n: Node): boolean => Node.string(n).trim() === '';
const uuidOf = (n: Node): string | null => (n as { uuid?: string }).uuid ?? null;

export const tagsAtBreak = (editor: Editor): TagsAtBreak => {
  if (!editor.selection) return { inline: [], block: [], footnotes: [], carrierUuid: null };
  const inline = Array.from(Editor.nodes(editor, { at: editor.selection, match: isTag }), ([n]) => (n as TagElement).tagId);
  const footnotes = Array.from(Editor.nodes(editor, { at: editor.selection, match: isFootnote }), ([n]) => (n as FootnoteElement).footnoteId).filter(
    (id): id is string => !!id
  );
  const carrier = Editor.above(editor, { at: Range.start(editor.selection), match: isTaggedTextCarrier });
  return {
    inline: [...new Set(inline)],
    block: carrier ? getBlockTagIds(carrier[0]) : [],
    footnotes: [...new Set(footnotes)],
    carrierUuid: carrier ? uuidOf(carrier[0]) : null,
  };
};

export const settleSplitFootnotes = (editor: Editor, footnoteIds: string[]): void => {
  footnoteIds.forEach((footnoteId) => {
    const pieces = Array.from(Editor.nodes(editor, { at: [], match: (n) => isFootnote(n) && n.footnoteId === footnoteId }));
    const blank = pieces.filter(([n]) => isBlank(n));
    if (blank.length === pieces.length) return;
    blank.reverse().forEach(([, path]) => Transforms.removeNodes(editor, { at: path }));
  });
};

const splitInlineTag = (
  editor: Editor,
  tagId: string,
  blockPath: Path,
  onId: string | null,
  blocker: TagContinuationBlocker | null,
  result: TagSplitResult
) => {
  const entries = Array.from(Editor.nodes(editor, { at: [], match: (n) => isTag(n) && n.tagId === tagId }));
  const newHalf = entries.filter(([, path]) => Path.isAncestor(blockPath, path));
  const firstHalf = entries.filter(([, path]) => Path.isBefore(path, blockPath) && !Path.isAncestor(path, blockPath));
  if (newHalf.length === 0 || firstHalf.length === 0) return;

  if (blocker) {
    newHalf.reverse().forEach(([, path]) => Transforms.unwrapNodes(editor, { at: path }));
    result.blocked.push({ tagId, reason: blocker });
    return;
  }
  const newTagId = uuidv4();
  firstHalf.forEach(([, path]) => Transforms.setNodes(editor, { continuedAt: newTagId } as Partial<TagElement>, { at: path }));
  newHalf.forEach(([, path]) => Transforms.setNodes(editor, { tagId: newTagId } as Partial<TagElement>, { at: path }));
  result.splits.push({ tagId, newTagId, onId });
};

const dropBinding = (editor: Editor, path: Path, tagId: string) =>
  writeBlockBindings(
    editor,
    path,
    getBlockTagBindings(Node.get(editor, path)).filter((b) => b.tagId !== tagId)
  );

const settleBlockBinding = (editor: Editor, tagId: string, carrierUuid: string | null, blocker: TagContinuationBlocker | null, result: TagSplitResult) => {
  const holders = Array.from(Editor.nodes(editor, { at: [], match: (n) => isTextCarrier(n) && getBlockTagIds(n).includes(tagId) }));
  const uuidTaken = () => !!carrierUuid && Array.from(Editor.nodes(editor, { at: [], match: (n) => uuidOf(n) === carrierUuid })).length > 0;

  if (holders.length === 1) {
    const [node, path] = holders[0];
    if (carrierUuid && uuidOf(node) !== carrierUuid && !uuidTaken()) Transforms.setNodes(editor, { uuid: carrierUuid } as Partial<Node>, { at: path });
    return;
  }
  if (holders.length !== 2) return;

  const [original, copy] = uuidOf(holders[1][0]) === carrierUuid ? [holders[1], holders[0]] : holders;
  if (isBlank(copy[0])) {
    dropBinding(editor, copy[1], tagId);
    return;
  }
  if (isBlank(original[0])) {
    dropBinding(editor, original[1], tagId);
    Transforms.setNodes(editor, { uuid: uuidv4() } as Partial<Node>, { at: original[1] });
    if (carrierUuid) Transforms.setNodes(editor, { uuid: carrierUuid } as Partial<Node>, { at: copy[1] });
    return;
  }
  if (blocker) {
    dropBinding(editor, copy[1], tagId);
    result.blocked.push({ tagId, reason: blocker });
    return;
  }

  const newTagId = uuidv4();
  const relink = (bindings: TagBinding[], fn: (b: TagBinding) => TagBinding) => bindings.map((b) => (b.tagId === tagId ? fn(b) : b));
  writeBlockBindings(
    editor,
    original[1],
    relink(getBlockTagBindings(Node.get(editor, original[1])), (b) => ({ ...b, continuedAt: newTagId }))
  );
  writeBlockBindings(
    editor,
    copy[1],
    relink(getBlockTagBindings(Node.get(editor, copy[1])), (b) => ({ ...b, tagId: newTagId }))
  );
  result.splits.push({ tagId, newTagId, onId: uuidOf(Node.get(editor, copy[1])) });
};

export const settleSplitTags = (editor: Editor, tags: TagsAtBreak, blockerFor: (tagId: string) => TagContinuationBlocker | null): TagSplitResult => {
  const result: TagSplitResult = { splits: [], blocked: [] };
  if (!editor.selection) return result;
  const blockEntry = Editor.above(editor, { at: editor.selection.anchor, match: (n) => Element.isElement(n) && Editor.isBlock(editor, n), mode: 'lowest' });
  if (!blockEntry) return result;
  const [block, blockPath] = blockEntry;
  const onId = uuidOf(block);

  Editor.withoutNormalizing(editor, () => {
    tags.inline.forEach((tagId) => splitInlineTag(editor, tagId, blockPath, onId, blockerFor(tagId), result));
    tags.block.forEach((tagId) => settleBlockBinding(editor, tagId, tags.carrierUuid, blockerFor(tagId), result));
  });
  return result;
};
